import { AppDownloader } from "@/app/shared/services/app-downloader/app-downloader";
import { hash } from "./string-helper";

export function getReadableFileSize(size: number): string {
  const sizes = ["KB", "MB", "GB", "TB"];

  let i = 0;
  do {
    size = size / 1024;
  } while (size > 1024 && ++i < sizes.length);

  return `${Math.round(size)} ${sizes[i]}`;
}

export function dataURLtoFile(dataUrl: string, filename?: string): File {
  const byteString = atob(dataUrl.split(',')[1]);
  const buffer = new ArrayBuffer(byteString.length);
  const data = new Uint8Array(buffer);
  for (let i = 0; i < byteString.length; i++) {
    data[i] = byteString.charCodeAt(i);
  }

  const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
  
  return new File([buffer], filename || hash(dataUrl), { type: mimeString });
}

export function getContentTypeByFilename(fileName: string): string {
  const mimeTypes = {
    "txt": "text/plain",
    "srt": "text/plain",
    "png": "image/png",
    "jpg": "image/jpeg",
    "jpeg": "image/jpeg",
    "tif": "image/tiff",
    "tiff": "image/tiff",
    "gif": "image/gif",
    "svg": "image/svg+xml",
    "webp": "image/webp",
    "pdf": "application/pdf",
    "zip": "application/zip",
    "mp3": "audio/mpeg",
    "mp4": "video/mp4",
    "mov": "video/quicktime",
    "wav": "audio/wav",
    "csv": "text/csv",
    "doc": "application/msword",
    "docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "xls": "application/vnd.ms-excel",
    "xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };

  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext && mimeTypes[ext] || "application/octet-stream";
}

export function isImageFile(fileName: string): boolean {
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico', 'tiff', 'tif'];
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext ? imageExtensions.includes(ext) : false;
}

function extractFilenameFromUrl(url: string): string | null {
  try {
    const pathname = new URL(url).pathname;
    const segment = pathname.split("/").pop();
    return segment && segment.includes(".") ? decodeURIComponent(segment) : null;
  } catch {
    return null;
  }
}

export async function downloadImage(url: string, filename?: string): Promise<void> {
  const resolvedFilename = filename || extractFilenameFromUrl(url) || "image";

  if (url.startsWith("blob:") || url.startsWith("data:")) {
    AppDownloader.download(url, resolvedFilename);
    return;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    AppDownloader.download(blobUrl, resolvedFilename);
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch {
    AppDownloader.open(url);
  }
}